/**
 * DeltaPill — period-over-period quantity change indicator.
 *
 * Renders a compact arrow + percent (or "New" / "—") next to a metric.
 * Driven entirely by the four fields the backend attaches when a caller
 * passes `include_comparison=true`:
 *
 *   previous_quantity, quantity_change_pct, quantity_change_direction
 *
 * Direction contract (lock with backend `_delta_fields`):
 *   up    → green   (positive percent)
 *   down  → red     (negative percent)
 *   flat  → muted   (|pct| < 0.05 OR both zero)
 *   new   → blue    (previous was zero, current > 0)
 */
import React from 'react';
import { ArrowUp, ArrowDown, Minus } from '@phosphor-icons/react';

const TONE = {
  up:   { fg: 'text-emerald-400', bg: 'bg-emerald-500/10', Icon: ArrowUp },
  down: { fg: 'text-rose-400',    bg: 'bg-rose-500/10',    Icon: ArrowDown },
  flat: { fg: 'text-zinc-500',    bg: 'bg-zinc-500/10',    Icon: Minus },
  new:  { fg: 'text-cyan-400',    bg: 'bg-cyan-500/10',    Icon: null },
};

export default function DeltaPill({
  direction,
  pct,
  size = 'xs',
  title,
  testId,
}) {
  if (!direction) {
    return (
      <span
        className="inline-flex items-center px-1 text-[10px] text-zinc-600"
        data-testid={testId}
        title="No comparison data"
      >—</span>
    );
  }

  const tone = TONE[direction] || TONE.flat;
  const sizing = size === 'sm'
    ? 'text-[11px] px-1.5 py-0.5 gap-0.5'
    : 'text-[10px] px-1 py-0.5 gap-0.5';

  if (direction === 'new') {
    return (
      <span
        className={`inline-flex items-center rounded ${tone.bg} ${tone.fg} ${sizing} font-mono uppercase tracking-wide`}
        title={title || 'No sales in the previous period'}
        data-testid={testId}
        data-direction="new"
      >New</span>
    );
  }

  const Icon = tone.Icon;
  const num = (pct == null)
    ? '—'
    : `${Math.abs(pct).toFixed(Math.abs(pct) < 10 ? 1 : 0)}%`;
  return (
    <span
      className={`inline-flex items-center rounded ${tone.bg} ${tone.fg} ${sizing} font-mono`}
      title={title}
      data-testid={testId}
      data-direction={direction}
      data-pct={pct ?? ''}
    >
      {Icon && <Icon size={10} weight="bold" />}{num}
    </span>
  );
}
